import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { Button, ExternalLinkButton } from '@/components/Buttons';
import { ResidentialUnitDocumentVersion, BuyerDocument } from '@/types';
import { getDownloadDocumentUrl, getOpenDocumentUrl } from '@/services/documents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/pro-light-svg-icons';
import { DateDisplay } from '@/components/localization';
import { getBuyerDocumentVersions } from '@/services/buyer-dashboard';
import { IState } from '@/state/IState';
import { useSelector } from 'react-redux';
import theme from './BuyerDocumentsHistoryDialog.module.scss';

interface Props {
    show: boolean;
    document: BuyerDocument;
    toggle: (state: boolean) => void;
}

const BuyerDocumentsHistoryDialog: React.FC<Props> = ({ show, document, toggle }) => {
    const [versions, setVersions] = useState<ResidentialUnitDocumentVersion[] | null>(null);

    const residentialUnitId = useSelector((state: IState) => state.buyer.permissions!.residentialUnitId);

    const handleCloseClick = () => {
        toggle(false);
    };

    const handleOpen = async () => {
        setVersions(await getBuyerDocumentVersions(residentialUnitId, document.documentId));
    };

    return (
        <Modal
            isOpen={show}
            toggle={handleCloseClick}
            onOpened={handleOpen}
            onClosed={() => setVersions(null)}>
            <ModalHeader>
                <Trans>Previous versions</Trans>
            </ModalHeader>
            <ModalBody>
                {!versions && <LoadingIndicator />}
                {versions && (
                    <table className="table style-4">
                        <thead>
                            <tr>
                                <th>
                                    <Trans>Uploaded</Trans>
                                </th>
                                <th>
                                    <Trans>Version</Trans>
                                </th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {versions.map((ver: ResidentialUnitDocumentVersion) => (
                                <tr key={ver.versionId}>
                                    <td>
                                        <DateDisplay value={ver.uploadDate} />
                                    </td>
                                    <td>{ver.versionNumber}</td>
                                    <td>
                                        <div className={theme.buttonWrapper}>
                                            <ExternalLinkButton
                                                href={getOpenDocumentUrl(ver.versionId)}
                                                target="_blank"
                                                rel="noreferrer noopener"
                                                color="tertiary"
                                                size="small">
                                                <Trans>Open</Trans>
                                            </ExternalLinkButton>
                                            <ExternalLinkButton
                                                href={getDownloadDocumentUrl(ver.versionId)}
                                                color="tertiary"
                                                size="small">
                                                <FontAwesomeIcon fixedWidth icon={faDownload} /> <Trans>Download</Trans>
                                            </ExternalLinkButton>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="tertiary" onClick={handleCloseClick}>
                    <Trans>Close</Trans>
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default BuyerDocumentsHistoryDialog;